import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import client from '../../api/client';
import { fetchTemplate, createTemplate, updateTemplate } from '../../api/templates';
import TemplatePreviewPanel from './TemplatePreviewPanel';
import styles from './TemplateBuilderPage.module.css';

const PROMPT_CHIPS = [
  { label: '5E Model', message: 'Create a 5E Model template with Engage, Explore, Explain, Elaborate, and Evaluate sections' },
  { label: 'Workshop Model', message: 'Create a Workshop Model template with Mini-Lesson, Work Time, and Share sections' },
  { label: 'Direct Instruction', message: 'Create a Direct Instruction template with I Do, We Do, You Do sections' },
  { label: 'Project-Based', message: 'Create a Project-Based Learning template with Driving Question, Research, Create, and Present sections' },
];

const HEADER_FIELDS = new Set(['title', 'grade_level', 'duration', 'subject']);

export default function TemplateBuilderPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const chipMessage = location.state?.chipMessage;

  // Template state
  const [templateName, setTemplateName] = useState('');
  const [sections, setSections] = useState([]);
  const [standardsPosition, setStandardsPosition] = useState(null);
  const [existingTemplate, setExistingTemplate] = useState(null);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);

  // Save state
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState(null);
  const [pageLoading, setPageLoading] = useState(!!templateId);

  const messagesEndRef = useRef(null);
  const autoSentRef = useRef(false);

  // Load existing template for edit mode
  useEffect(() => {
    if (!templateId) return;
    (async () => {
      try {
        const data = await fetchTemplate(templateId);
        const tmpl = data.data || data;
        setExistingTemplate(tmpl);
        setTemplateName(tmpl.name || '');

        // Parse placeholders into sections, filtering out header fields
        if (tmpl.content) {
          const content = tmpl.content;
          const sectionRegex = /## (.+)\n<!-- type: (\w+) -->\n\{\{ (\w+) \}\}/g;
          const parsed = [];
          let match;
          while ((match = sectionRegex.exec(content)) !== null) {
            const [, displayName, type, name] = match;
            if (!HEADER_FIELDS.has(name)) {
              parsed.push({ name, displayName, type });
            }
          }
          setSections(parsed);
        }

        setPreviewVisible(true);
      } catch (err) {
        setError('Failed to load template');
      } finally {
        setPageLoading(false);
      }
    })();
  }, [templateId]);

  // Auto-send chip message from sidebar quick-start
  useEffect(() => {
    if (autoSentRef.current) return;
    if (pageLoading) return;
    if (!chipMessage) return;

    autoSentRef.current = true;

    // Clear route state to prevent re-send on refresh
    window.history.replaceState({}, '', window.location.pathname);

    setTimeout(() => {
      sendMessageWithText(chipMessage);
    }, 300);
  }, [pageLoading, chipMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatLoading]);

  // Process template updates from AI response
  const processTemplateUpdates = useCallback((updates) => {
    if (!updates || !Array.isArray(updates)) return;

    const templateUpdates = updates.filter(u => u.section === 'templateUpdate');
    if (templateUpdates.length === 0) return;

    templateUpdates.forEach(update => {
      const { action } = update.content || {};

      switch (action) {
        case 'add_section': {
          const { section_name, display_name, section_type, position } = update.content;
          setSections(prev => {
            if (prev.some(s => s.name === section_name)) return prev;
            const newSection = {
              name: section_name,
              displayName: display_name || section_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
              type: section_type || 'text',
            };
            if (position !== undefined && position >= 0 && position <= prev.length) {
              const updated = [...prev];
              updated.splice(position, 0, newSection);
              return updated;
            }
            return [...prev, newSection];
          });
          setPreviewVisible(true);
          break;
        }
        case 'remove_section': {
          const { section_name } = update.content;
          setSections(prev => prev.filter(s => s.name !== section_name));
          break;
        }
        case 'rename_section': {
          const { section_name, display_name } = update.content;
          setSections(prev =>
            prev.map(s =>
              s.name === section_name ? { ...s, displayName: display_name } : s
            )
          );
          break;
        }
        case 'reorder_sections': {
          const { section_order } = update.content;
          setSections(prev => {
            const byName = {};
            prev.forEach(s => { byName[s.name] = s; });
            const reordered = [];
            section_order.forEach(name => {
              if (byName[name]) {
                reordered.push(byName[name]);
                delete byName[name];
              }
            });
            // Append any sections not in the order list
            Object.values(byName).forEach(s => reordered.push(s));
            return reordered;
          });
          break;
        }
        case 'set_type': {
          const { section_name, type } = update.content;
          setSections(prev =>
            prev.map(s =>
              s.name === section_name ? { ...s, type } : s
            )
          );
          break;
        }
        case 'set_name': {
          const { template_name } = update.content;
          setTemplateName(template_name);
          setPreviewVisible(true);
          break;
        }
        default:
          break;
      }
    });
  }, []);

  // Chat send
  const sendMessageWithText = async (text) => {
    const userMessage = { role: 'user', text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setChatLoading(true);

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await client.post('/api/chat', {
        message: text,
        conversationId,
        mode: 'template_builder',
      }, { headers });

      setMessages(prev => [...prev, {
        role: 'assistant',
        text: response.data.text,
      }]);

      if (response.data.conversationId) {
        setConversationId(response.data.conversationId);
      }

      if (response.data.lessonPlanUpdates) {
        processTemplateUpdates(response.data.lessonPlanUpdates);
      }
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Sorry, something went wrong. Please try again.',
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    await sendMessageWithText(input.trim());
  };

  // Save template
  const handleSave = async () => {
    if (!templateName.trim()) {
      setError('Template name is required');
      return;
    }
    if (sections.length === 0) {
      setError('At least one section is required');
      return;
    }

    setSaving(true);
    setError(null);

    // Build markdown with standards at the correct position
    const frontmatter = `---\nname: ${templateName}\ndescription: Custom lesson template\n---`;
    const stdPos = standardsPosition ?? sections.length;
    const bodyParts = [];
    let sIdx = 0;
    for (let i = 0; i <= sections.length; i++) {
      if (i === stdPos) {
        bodyParts.push(`## Standards Alignment\n<!-- type: list -->\n{{ standards }}`);
      }
      if (sIdx < sections.length && i === sIdx) {
        const s = sections[sIdx];
        bodyParts.push(`## ${s.displayName}\n<!-- type: ${s.type} -->\n{{ ${s.name} }}`);
        sIdx++;
      }
    }
    if (stdPos > sections.length) {
      bodyParts.push(`## Standards Alignment\n<!-- type: list -->\n{{ standards }}`);
    }
    const markdown = `${frontmatter}\n\n${bodyParts.join('\n\n')}`;

    try {
      if (existingTemplate?.templateId) {
        await updateTemplate(existingTemplate.templateId, markdown);
      } else {
        await createTemplate(markdown);
      }
      setJustSaved(true);
      setTimeout(() => {
        navigate('/templates');
      }, 600);
    } catch (err) {
      setError('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  if (pageLoading) {
    return (
      <div className={styles.pageLoading}>
        <div className={styles.pageSpinner} />
        <span>Loading template...</span>
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${!previewVisible ? styles.chatOnly : ''}`}>
      {/* Chat Panel */}
      <div className={`${styles.chatPanel} ${!previewVisible ? styles.chatPanelFull : ''}`}>
        <div className={styles.chatHeader}>
          <div className={styles.headerIcon}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <h2 className={styles.chatTitle}>
            {templateId ? 'Edit Template' : 'Create Template'}
          </h2>
          <Link to="/templates" className={styles.backLink}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Templates
          </Link>
        </div>

        {error && (
          <div className={styles.errorBanner}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
          </div>
        )}

        {/* Messages */}
        <div className={styles.messagesContainer}>
          {messages.length === 0 && (
            <div className={styles.welcomeContainer}>
              <img
                src="/logo-notext.svg"
                alt="Teachio"
                className={styles.welcomeIcon}
                style={{ width: '64px', height: '64px', objectFit: 'contain' }}
              />
              <h3 className={styles.welcomeTitle}>Template Builder</h3>
              <p className={styles.welcomeText}>
                Describe the lesson template structure you want to create, or pick a starting point below.
              </p>
              <div className={styles.suggestionsGrid}>
                {PROMPT_CHIPS.map((chip, i) => (
                  <button
                    key={i}
                    type="button"
                    className={styles.suggestionChip}
                    onClick={() => sendMessageWithText(chip.message)}
                    disabled={chatLoading}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`${styles.message} ${msg.role === 'user' ? styles.userMessage : styles.assistantMessage}`}
            >
              <div className={styles.messageAvatar}>
                {msg.role === 'user' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                  </svg>
                )}
              </div>
              <div className={styles.messageContent}>
                <span className={styles.messageRole}>
                  {msg.role === 'user' ? 'You' : 'Assistant'}
                </span>
                <p className={styles.messageText}>{msg.text}</p>
              </div>
            </div>
          ))}

          {chatLoading && (
            <div className={styles.loadingBubble}>
              <div className={styles.loadingSpinner}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--primary-500)" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
              </div>
              <span className={styles.loadingText}>Building your template...</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className={styles.inputContainer}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Describe your template structure..."
            className={styles.chatInput}
            disabled={chatLoading}
          />
          <button
            onClick={sendMessage}
            disabled={chatLoading || !input.trim()}
            className={`${styles.sendButton} ${chatLoading || !input.trim() ? styles.sendButtonDisabled : ''}`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>

      {/* Preview Panel */}
      {previewVisible && (
        <div className={styles.documentPanel}>
          <div className={styles.documentWrapper}>
            <TemplatePreviewPanel
              templateName={templateName}
              sections={sections}
              onUpdateName={setTemplateName}
              onUpdateSections={setSections}
              standardsPosition={standardsPosition}
              onUpdateStandardsPosition={setStandardsPosition}
            />
          </div>
          <div className={styles.saveBar}>
            <span className={styles.saveInfo}>
              {sections.length} section{sections.length !== 1 ? 's' : ''}
            </span>
            <button
              className={`${styles.saveButton} ${justSaved ? styles.saveButtonSaved : ''}`}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                  Saving...
                </>
              ) : justSaved ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Saved!
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {existingTemplate ? 'Save Changes' : 'Save Template'}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
